import { useState, useRef } from 'react'
import React from 'react'
import { Upload, File, X, CheckCircle, AlertCircle, Download, Trash2 } from 'lucide-react'

interface UploadedFile {
  name: string
  original_filename: string
  url: string
  size: number
  description?: string
}

interface MinIOFile {
  name: string
  size: number
  last_modified: string | null
  url: string
}

function FileUploadInterface() {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [minioFiles, setMinioFiles] = useState<MinIOFile[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [isLoadingFiles, setIsLoadingFiles] = useState(false)
  const [uploadStatus, setUploadStatus] = useState<string>('')
  const [description, setDescription] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const loadMinioFiles = async () => {
    setIsLoadingFiles(true)
    try {
      const response = await fetch('/api/files/list')
      const data = await response.json()
      
      if (data.success) {
        setMinioFiles(data.files)
      } else {
        console.error('Failed to load files:', data.error)
      }
    } catch (error) {
      console.error('Error loading files:', error)
    } finally {
      setIsLoadingFiles(false)
    }
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    setIsUploading(true)
    setUploadStatus('')

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('description', description || '')

        const response = await fetch('/api/upload/file', {
          method: 'POST',
          body: formData,
        })

        const result = await response.json()

        if (result.success) {
          setUploadedFiles(prev => [...prev, result])
          setUploadStatus(`✅ ${file.name} uploaded successfully`)
        } else {
          setUploadStatus(`❌ Failed to upload ${file.name}: ${result.error}`)
        }
      } catch (error) {
        console.error('Upload error:', error)
        setUploadStatus(`❌ Failed to upload ${file.name}`)
      }
    }

    setIsUploading(false)
    setDescription('')
    
    // Reload MinIO files after upload
    await loadMinioFiles()
  }

  const deleteFile = async (filename: string) => {
    try {
      const response = await fetch(`/api/files/${filename}`, {
        method: 'DELETE',
      })
      
      const result = await response.json()
      
      if (result.success) {
        setUploadStatus(`🗑️ File ${filename} deleted successfully`)
        await loadMinioFiles()
      } else {
        setUploadStatus(`❌ Failed to delete ${filename}: ${result.error}`)
      }
    } catch (error) {
      console.error('Delete error:', error)
      setUploadStatus(`❌ Failed to delete ${filename}`)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Load files on component mount
  React.useEffect(() => {
    loadMinioFiles()
  }, [])

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-purple-900/20 to-blue-900/20 p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white mb-2">📁 File Management</h2>
        <p className="text-purple-300">Upload files to MinIO storage and manage your documents</p>
      </div>

      {/* Upload Section */}
      <div className="bg-black/30 backdrop-blur-md rounded-2xl p-6 mb-6 border border-purple-500/30">
        <h3 className="text-lg font-semibold text-white mb-4">Upload Files</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-purple-300 mb-2">
              Description (optional)
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add a description for your files..."
              className="w-full bg-black/30 backdrop-blur-md border border-purple-500/30 rounded-lg px-3 py-2 text-white placeholder-purple-400 focus:outline-none focus:border-purple-400"
            />
          </div>

          <div className="flex items-center space-x-4">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileUpload}
              className="hidden"
              accept="*/*"
            />
            
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:from-gray-600 disabled:to-gray-700 text-white px-6 py-3 rounded-lg transition-all transform hover:scale-105 shadow-lg flex items-center space-x-2"
            >
              <Upload className="w-5 h-5" />
              <span>{isUploading ? 'Uploading...' : 'Choose Files'}</span>
            </button>

            <button
              onClick={loadMinioFiles}
              disabled={isLoadingFiles}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-6 py-3 rounded-lg transition-all transform hover:scale-105 shadow-lg flex items-center space-x-2"
            >
              <File className="w-5 h-5" />
              <span>{isLoadingFiles ? 'Loading...' : 'Refresh Files'}</span>
            </button>
          </div>

          {uploadStatus && (
            <div className={`p-3 rounded-lg ${
              uploadStatus.startsWith('✅') 
                ? 'bg-green-500/20 border border-green-500/30' 
                : 'bg-red-500/20 border border-red-500/30'
            }`}>
              <p className="text-white">{uploadStatus}</p>
            </div>
          )}
        </div>
      </div>

      {/* Files List */}
      <div className="flex-1 bg-black/30 backdrop-blur-md rounded-2xl p-6 border border-purple-500/30">
        <h3 className="text-lg font-semibold text-white mb-4">Stored Files ({minioFiles.length})</h3>
        
        {minioFiles.length === 0 ? (
          <div className="text-center py-8">
            <File className="w-16 h-16 mx-auto mb-4 text-purple-500 opacity-50" />
            <p className="text-purple-300">No files uploaded yet</p>
            <p className="text-purple-400 text-sm mt-2">Upload some files to get started</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {minioFiles.map((file) => (
              <div key={file.name} className="bg-black/20 rounded-lg p-4 border border-purple-500/20">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-white font-medium truncate">{file.name}</h4>
                    <p className="text-purple-300 text-sm">{formatFileSize(file.size)}</p>
                    {file.last_modified && (
                      <p className="text-purple-400 text-xs">
                        {new Date(file.last_modified).toLocaleString()}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-lg transition-colors"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                    
                    <button
                      onClick={() => deleteFile(file.name)}
                      className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default FileUploadInterface
